import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";

import { DndContext, closestCenter } from "@dnd-kit/core";

import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

const STATUS_CONFIG = {
  "In Progress": {
    icon: AutorenewIcon,
    color: "#2563eb",
    bg: "#eff6ff",
    description: "This weeks Requests currently being processed",
  },
  "On Hold": {
    icon: PauseCircleOutlineIcon,
    color: "#7c3aed",
    bg: "#f5f3ff",
    description: "This weeks Requests paused awaiting action",
  },
  Pending: {
    icon: HourglassEmptyIcon,
    color: "#f59e0b",
    bg: "#fffbeb",
    description: "This weeks Requests awaiting review",
  },
  Approved: {
    icon: CheckCircleOutlineIcon,
    color: "#16a34a",
    bg: "#f0fdf4",
    description: "This weeks Successfully approved requests",
  },
  Rejected: {
    icon: CancelOutlinedIcon,
    color: "#dc2626",
    bg: "#fef2f2",
    description: "This weeks Requests that were rejected",
  },
    "Validation Failed" : {
    icon: CancelOutlinedIcon,
    color: "#dc2626",
    bg: "#fef2f2",
    description: "This weeks Requests that were rejected due to validation errors",
  },
  
};

const toLocalDateKey = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toDateKey = (value) => {
  if (!value || value === "NaN") return null;

  if (typeof value === "number" || /^\d+$/.test(String(value))) {
    const date = new Date(Number(value));
    return Number.isNaN(date.getTime()) ? null : toLocalDateKey(date);
  }

  const text = String(value).trim();
  const ddmmyyyy = text.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (ddmmyyyy) {
    const [, dd, mm, yyyy] = ddmmyyyy;
    return `${yyyy}-${mm}-${dd}`;
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : toLocalDateKey(date);
};

const formatDateLabel = (dateKey) => {
  if (!dateKey || dateKey === "Current") return dateKey;
  const [yyyy, mm, dd] = dateKey.split("-").map(Number);
  return new Date(yyyy, mm - 1, dd).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const getLastDateKeys = (endKey, length = 8) => {
  const [yyyy, mm, dd] = endKey.split("-").map(Number);
  const endDate = new Date(yyyy, mm - 1, dd);

  return Array.from({ length }, (_, index) => {
    const date = new Date(endDate);
    date.setDate(endDate.getDate() - (length - 1 - index));
    return toLocalDateKey(date);
  });
};

const buildStatusChartData = (rows, status, count, mode) => {
  const datedRows = rows
    .map((row) => ({ ...row, chartDate: toDateKey(row.request_date) }))
    .filter((row) => row.chartDate);

  if (datedRows.length === 0) {
    return [
      { label: "Current", v: count },
      { label: "Current", v: count },
    ];
  }

  const todayKey = toLocalDateKey(new Date());
  const sortedDateKeys = [
    ...new Set(datedRows.map((row) => row.chartDate)),
  ].sort();
  const latestDataKey = sortedDateKeys[sortedDateKeys.length - 1];
  const endKey =
    latestDataKey && latestDataKey > todayKey ? latestDataKey : todayKey;
  const dateKeys = getLastDateKeys(endKey);
  let runningTotal = 0;

  return dateKeys.map((dateKey) => ({
    label: formatDateLabel(dateKey),
    rawDate: dateKey,
    v:
      mode === "cumulative"
        ? (runningTotal += datedRows.filter(
            (row) => row.chartDate === dateKey && row.status === status,
          ).length)
        : datedRows.filter(
            (row) => row.chartDate === dateKey && row.status === status,
          ).length,
  }));
};

const getTodayCount = (rows, status) => {
  const todayKey = toLocalDateKey(new Date());
  return rows.filter(
    (row) => row.status === status && toDateKey(row.request_date) === todayKey,
  ).length;
};

// ── Count requests within the same trailing window the sparkline shows ────
const getWeekCount = (rows, status) => {
  const datedRows = rows
    .map((row) => ({ ...row, chartDate: toDateKey(row.request_date) }))
    .filter((row) => row.chartDate);

  if (datedRows.length === 0) return 0;

  const todayKey = toLocalDateKey(new Date());
  const sortedDateKeys = [
    ...new Set(datedRows.map((row) => row.chartDate)),
  ].sort();
  const latestDataKey = sortedDateKeys[sortedDateKeys.length - 1];
  const endKey =
    latestDataKey && latestDataKey > todayKey ? latestDataKey : todayKey;
  const weekKeys = new Set(getLastDateKeys(endKey));

  return datedRows.filter(
    (row) => row.status === status && weekKeys.has(row.chartDate),
  ).length;
};

const formatUpdatedTime = (date) => {
  if (!date) return "";
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;

  return (
    <Box
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: "6px",
        backgroundColor: "#ffffff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#334155" }}>
        {item?.label}
      </Typography>
      <Typography sx={{ fontSize: 11, color: "#64748b" }}>
        Count: {payload[0]?.value ?? 0}
      </Typography>
    </Box>
  );
};

const StatusCard = ({
  status,
  count,
  chartData,
  todayCount,
  selected,
  onClick,
  dragHandleProps, // ✅ { attributes, listeners } passed down from SortableCard
  isDragging,
}) => {
  const config = STATUS_CONFIG[status];
  if (!config) return null;
  const Icon = config.icon;
  const startLabel = chartData[0]?.label || "";
  const endLabel = chartData[chartData.length - 1]?.label || "";

  return (
    <Box
      onClick={() => onClick?.(status)}
      // ✅ "group" class hook — lets the drag handle reveal itself only
      // while this specific card is hovered, via the sx selector below
      className="status-card-root"
      sx={{
        position: "relative",
        p: 2.5,
        borderRadius: "14px",
        backgroundColor: "#ffffff",
        border: selected ? `1px solid ${config.color}` : "1px solid #e2e8f0",
        boxShadow: selected
          ? `0 10px 26px ${config.color}24`
          : "0 4px 16px rgba(15, 23, 42, 0.04)",
        cursor: "pointer",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition:
          "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        opacity: isDragging ? 0.5 : 1,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 10px 26px ${config.color}1f`,
        },
        // ✅ reveal the drag handle only when this card is hovered
        "&:hover .drag-handle": {
          opacity: 1,
        },
      }}
    >
      {/* ✅ Drag handle — the ONLY part of the card that initiates a drag.
          Hidden by default, fades in on card hover, and stops the click
          from bubbling up to the card's onClick (selection) handler. */}
      <Box
        className="drag-handle"
        {...(dragHandleProps?.attributes || {})}
        {...(dragHandleProps?.listeners || {})}
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          width: 22,
          height: 22,
          borderRadius: "6px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#94a3b8",
          opacity: 0,
          transition: "opacity 150ms ease, background-color 150ms ease",
          cursor: isDragging ? "grabbing" : "grab",
          "&:hover": {
            backgroundColor: "#f1f5f9",
            color: "#475569",
          },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            backgroundColor: config.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ fontSize: 18, color: config.color }} />
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#475569" }}>
          {status}
        </Typography>
      </Box>

      <Typography
        sx={{ fontSize: 28, fontWeight: 900, color: "#0f172a", lineHeight: 1 }}
      >
        {count.toLocaleString()}
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          mt: 0.5,
          mb: 1.25,
        }}
      >
        <Typography sx={{ fontSize: 11, color: "#94a3b8", flex: 1 }}>
          {config.description}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            color: config.color,
            whiteSpace: "nowrap",
          }}
        >
          Today {todayCount}
        </Typography>
      </Box>

      <Box sx={{ height: 36, mt: "auto" }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          >
            <RechartsTooltip
              content={<ChartTooltip />}
              cursor={{ stroke: config.color, strokeOpacity: 0.2 }}
            />
            <Area
              type="monotone"
              dataKey="v"
              stroke={config.color}
              fill={config.color}
              fillOpacity={0.15}
              strokeWidth={2}
              dot={false}
              isAnimationActive
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
          {startLabel}
        </Typography>
        <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
          {endLabel}
        </Typography>
      </Box>
    </Box>
  );
};

const SortableCard = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // ✅ no more {...attributes} / {...listeners} on the whole card — only the
  // drag handle inside StatusCard receives them now, via render prop below.
  return (
    <Box ref={setNodeRef} style={style}>
      {typeof children === "function"
        ? children({ attributes, listeners, isDragging })
        : children}
    </Box>
  );
};

const MCTStatusCards = ({
  statusCounts,
  requestData = [],
  selectedStatus,
  onStatusClick,
  lastUpdated,
}) => {
  const [chartMode] = React.useState("daily");
  const defaultStatuses = [
    "In Progress",
    "On Hold",
    "Pending",
    "Approved",
    "Rejected",
    "Validation Failed",
    // "Testing",
  ];

  const [orderedStatuses, setOrderedStatuses] = React.useState(defaultStatuses);

  React.useEffect(() => {
    const savedOrder = localStorage.getItem("statusCardOrder");

    if (savedOrder) {
      const parsed = JSON.parse(savedOrder);
      // Merge so newly added statuses still appear even with a saved order
      const merged = [
        ...parsed.filter((status) => defaultStatuses.includes(status)),
        ...defaultStatuses.filter((status) => !parsed.includes(status)),
      ];
      setOrderedStatuses(merged);
    }
  }, []);

  React.useEffect(() => {
    localStorage.setItem("statusCardOrder", JSON.stringify(orderedStatuses));
  }, [orderedStatuses]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setOrderedStatuses((items) => {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);

      return arrayMove(items, oldIndex, newIndex);
    });
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedStatuses} strategy={rectSortingStrategy}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(6, 1fr)",
              },
              gap: 2,
              flex: 1,
            }}
          >
            {orderedStatuses.map((status) => {
              const weekCount = getWeekCount(requestData, status);

              return (
                <SortableCard key={status} id={status}>
                  {({ attributes, listeners, isDragging }) => (
                    <StatusCard
                      status={status}
                      count={weekCount}
                      chartData={buildStatusChartData(
                        requestData,
                        status,
                        weekCount,
                        chartMode,
                      )}
                      todayCount={getTodayCount(requestData, status)}
                      selected={selectedStatus === status}
                      onClick={onStatusClick}
                      dragHandleProps={{ attributes, listeners }}
                      isDragging={isDragging}
                    />
                  )}
                </SortableCard>
              );
            })}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default MCTStatusCards;